/**
 * Minimal WebSocket subscription hook for /ws/jobs.
 * Auto-reconnects with exponential backoff.
 */
import { useEffect, useRef } from "react";

import { getAccessToken } from "@/lib/auth";
import { useJobEventsStore } from "@/state/jobEventsStore";

const WS_BASE =
  (import.meta.env.VITE_WS_BASE_URL || undefined) ??
  (() => {
    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    return `${proto}//${location.host}/ws`;
  })();

export interface JobEvent {
  type: string;
  file_id?: string;
  status: string;
  progress: number;
  message?: string | null;
  ts?: string;
}

let _socketConsumers = 0;

function useJobEventsConnection(): void {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptRef = useRef(0);

  useEffect(() => {
    _socketConsumers += 1;
    let stopped = false;

    const connect = () => {
      if (stopped) return;
      const ws = new WebSocket(`${WS_BASE}/jobs`);
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttemptRef.current = 0;
        // A credencial vai no primeiro frame, e não em `?token=` (F-S6, T-36):
        // a query string ia em texto claro para o access log do nginx a cada
        // reconexão. Sem token o servidor fecha com 1008, como já fechava.
        ws.send(JSON.stringify({ type: "auth", token: getAccessToken() }));
      };

      ws.onmessage = (msg) => {
        try {
          const data = JSON.parse(msg.data) as JobEvent;
          useJobEventsStore.getState().pushEvent(data);
        } catch {
          /* ignore non-json messages */
        }
      };

      const scheduleReconnect = () => {
        if (stopped) return;
        const attempt = ++reconnectAttemptRef.current;
        const delay = Math.min(1000 * 2 ** attempt, 15000);
        setTimeout(connect, delay);
      };

      ws.onclose = scheduleReconnect;
      ws.onerror = () => ws.close();
    };

    connect();

    return () => {
      _socketConsumers -= 1;
      if (_socketConsumers <= 0) {
        useJobEventsStore.getState().reset();
        _socketConsumers = 0;
      }
      stopped = true;
      wsRef.current?.close();
    };
  }, []);
}

export function useLastRelevantJobEvent(): JobEvent | null {
  useJobEventsConnection();
  return useJobEventsStore((state) => state.lastRelevantEvent);
}
